<script setup>
import { computed, reactive } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'
import Loading from '../../../../components/Loading.vue'
import StatusBadge from '../../../../components/StatusBadge.vue'
import {
  KkIcon,
  KkModal
} from '../../../../components/base'
import { KkInfo, KkErrorModal } from '../../../../components/ui'
import { KkTutorial } from '../../../../components/projects'
import templateImagePath from '../../../../../../images/mapion_biz/template'
import moment from 'moment'
const store = useStore()
const { t } = useI18n()
const templates = computed(() => store.state.companiesMapionBizBannersTemplates.data)
const templateLoading = computed(() => store.state.companiesMapionBizBannersTemplates.loading)
const readMapionBizBannersTemplates = () => store.dispatch('companiesMapionBizBannersTemplates/readMapionBizBannersTemplates')
const deleteMapionBizBannersTemplate = (templateId) => store.dispatch('companiesMapionBizBannersTemplates/deleteMapionBizBannersTemplate', templateId)
const downloadTemplate = (templateId) => store.dispatch('companiesMapionBizBannersTemplatesCsvDownloads/downloadTemplate', templateId)
const downloadCsv = (templateId, filename) => store.dispatch('companiesMapionBizBannersCsvImportResultCsvDownloads/downloadCsv', { templateId, filename })
const importCsv = (templateId, data) => store.dispatch('companiesMapionBizBannersTemplatesCsvImports/importCsv', { templateId, data })
const { company_uuid } = useRoute().params
defineEmits(['update-title', 'update-breadcrumbs', 'update-progress'])
const state = reactive({
  csvModalTemplate: null,
  openErrors: null
})
const handleDelete = async (templateId) => {
  if(!confirm(t('pages.companies.mapionBizBannerTemplates.deleteConfirm'))) return
  await deleteMapionBizBannersTemplate(templateId)
  readMapionBizBannersTemplates()
}
const extractStatusNumber = status => {
  switch (status) {
  case 'complete':
    return 1
  case 'error':
    return 2
  case 'progress':
    return 3
  }
}
const selectedFile = async (e) => {
  e.preventDefault()
  const data = new FormData()
  data.append('csv[file]', e.target.files[0])
  await importCsv(state.csvModalTemplate.id, data)
  state.csvModalTemplate = null
  readMapionBizBannersTemplates()

}
const load = async () => {
  await readMapionBizBannersTemplates()
}

load()
</script>

<template>
  <div>
    <div class="p-box">
      <Loading v-if="templateLoading" />
      <KkTutorial
        v-else-if="templates.length === 0"
        :description1="t('pages.companies.mapionBizBannerTemplates.noFile.description')"
        :img-path="templateImagePath"
        :tutorial-description="t('pages.companies.mapionBizBannerTemplates.tutorial.description')"
        class="noTemplates"
      >
        <template #buttonArea>
          <div class="c-button noTemplates__button">
            <router-link
              :to="{ name: 'CompaniesManagement', params: { company_uuid: company_uuid } }"
              class="c-button__item c-button__item--outline-cancel"
            >
              {{ $t('labels.cancel') }}
            </router-link>
          </div>
          <div class="c-button noTemplates__button">
            <router-link :to="{ name: 'CompaniesMapionBannerTemplateNew', params: { company_uuid: company_uuid }}"
                         class="c-button__item c-button__item--primary"
            >
              {{ t('pages.companies.mapionBizBannerTemplates.tutorial.makeMasterData') }}
            </router-link>
          </div>
        </template>
      </KkTutorial>
      <template v-else>
        <div class="p-box__body">
          <KkInfo icon="hint">
            {{ t('pages.companies.mapionBizBannerTemplates.info') }}
          </KkInfo>
          <div class="p-table__content">
            <table class="templateTable">
              <thead>
                <tr>
                  <th class="templateTable__nameHeader">
                    {{ t('pages.companies.mapionBizBannerTemplates.name') }}
                  </th>
                  <th class="templateTable__elementHeader">
                    {{ t('pages.companies.mapionBizBannerTemplates.header') }}
                  </th>
                  <th class="templateTable__elementHeader">
                    {{ t('pages.companies.mapionBizBannerTemplates.footer') }}
                  </th>
                  <th />
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(template, i) in templates"
                  :key="'template_' + i"
                  :class="{ 'templateTable__tr--closed': template.status === 'progress' }"
                >
                  <td>
                    <div class="templateTable__name">{{ template.name }}</div>
                    <div v-if="template.status !== 'progress'" class="templateTable__item">
                      {{ $t('pages.companies.mapionBizBannerTemplates.updateAt', { time: moment(template.updatedAt).format(t('format.date.default')) }) }}
                    </div>
                    <KkInfo v-if="template.status === 'not_imported'" icon="hint" class="templateTable__item">
                      {{ t('pages.companies.mapionBizBannerTemplates.notImported') }}
                    </KkInfo>
                    <div v-if="template.status === 'complete'" class="templateTable__item">
                      {{ $t('pages.companies.mapionBizBannerTemplates.useStore') }}
                      <button class="openModal" @click="state.openStores = template.stores">
                        {{ $t('pages.companies.mapionBizBannerTemplates.storeCount', { number: template.stores.length }) }}
                      </button>
                    </div>
                    <span v-if="['progress', 'complete'].includes(template.status)">
                      <StatusBadge :status="extractStatusNumber(template.status)" />
                      {{ $t('pages.companies.mapionBizBannerTemplates.status.' + template.status) }}
                    </span>
                    <span v-else-if="template.status === 'error'" class="openModal" @click="state.openErrors = template.errors">
                      <StatusBadge :status="extractStatusNumber(template.status)" />
                      {{ $t('pages.companies.mapionBizBannerTemplates.status.' + template.status) }}
                    </span>
                  </td>
                  <td class="elementContainer">
                    <div v-if="template.headers.length === 0">{{ $t('pages.companies.mapionBizBannerTemplates.notSetting') }}</div>
                    <div v-for="(header, j) in template.headers" v-else
                         :key="'header' + j"
                         class="elementContainer__list"
                    >
                      <div>{{ $t(`pages.companies.mapionBizBannerTemplateForm.kind.${ header.kind }.label`) }}</div>
                      <div v-if="header.name">{{ header.name }}</div>
                    </div>
                  </td>
                  <td class="elementContainer">
                    <div v-if="template.footers.length === 0">{{ $t('pages.companies.mapionBizBannerTemplates.notSetting') }}</div>
                    <div v-for="(footer, j) in template.footers" v-else
                         :key="'footer' + j"
                         class="elementContainer__list"
                    >
                      <div>{{ $t(`pages.companies.mapionBizBannerTemplateForm.kind.${ footer.kind }.label`) }}</div>
                      <div v-if="footer.name">{{ footer.name }}</div>
                    </div>
                  </td>
                  <td>
                    <div class="card__action c-button c-button--icon">
                      <template v-if="template.status !== 'progress'">
                        <button class="c-button__item" @click="state.csvModalTemplate = template">
                          <KkIcon name="iconPaper" class="c-button__icon" />
                          {{ $t('pages.companies.mapionBizBannerTemplates.csvManage') }}
                        </button>
                        <router-link v-if="!['complete', 'error'].includes(template.status)" :to="{ name: 'CompaniesMapionBannerTemplateEdit', params: { company_uuid: company_uuid, template_id: template.id } }" class="c-button__item">
                          <KkIcon name="iconPencil" class="c-button__icon" />
                          {{ $t('labels.edit') }}
                        </router-link>
                        <router-link :to="{ name: 'CompaniesMapionBannerTemplateNew', params: { company_uuid: company_uuid }, query: { template_id: template.id } }"
                                     class="c-button__item"
                        >
                          <KkIcon name="copy" class="c-button__icon" />
                          {{ $t('labels.copy') }}
                        </router-link>
                        <button class="c-button__item" @click="handleDelete(template.id)">
                          <KkIcon name="iconCross" class="c-button__icon" />
                          {{ $t('labels.delete') }}
                        </button>
                      </template>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
            <router-link :to="{ name: 'CompaniesMapionBannerTemplateNew', params: { company_uuid: company_uuid } }">
              <button class="addTemplate" type="button">
                <KkIcon name="iconPlusRed" />
                <span class="addLabel">{{ $t('pages.companies.mapionBizBannerTemplates.addLabel') }}</span>
              </button>
            </router-link>
          </div>
        </div>
      </template>
    </div>
    <KkModal :is-visible="!!state.csvModalTemplate" @change="state.csvModalTemplate = null">
      <div class="content__header">
        {{ $t('pages.companies.mapionBizBannerTemplates.csvManage') }}
      </div>
      <div class="content__body">
        <KkInfo icon="hint">{{ $t('pages.companies.mapionBizBannerTemplates.csvModal.info') }}</KkInfo>
        <div class="csvManage">
          <div class="csvManage__header">
            {{ state.csvModalTemplate.name }}
          </div>
          <div class="csvManage__contents">
            <label class="importCsv" type="button">
              <input id="fileUpload" type="file" name="file" class="u-hidden" @change="selectedFile">
              <div class="importCsv__button">
                <KkIcon name="iconPlusRed" />
                <span class="addLabel">{{ $t('pages.companies.mapionBizBannerTemplates.csvModal.import') }}</span>
              </div>
            </label>
            <div class="downloadContainer">
              <button class="downloadContainer__button" @click="downloadTemplate(state.csvModalTemplate.id)">
                <KkIcon name="iconDownload" />{{ $t('pages.companies.mapionBizBannerTemplates.csvModal.downloadTemplate') }}
              </button>
              <button v-if="state.csvModalTemplate.status !== 'not_imported'" class="downloadContainer__button" @click="downloadCsv(state.csvModalTemplate.id, state.csvModalTemplate.name)">
                <KkIcon name="iconDownload" />{{ $t('pages.companies.mapionBizBannerTemplates.csvModal.downloadContents') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </KkModal>
    <KkErrorModal
      :show-modal="!!state.openErrors"
      :errors="state.openErrors || []"
      target-label="pages.companies.mapionBizBannerTemplates.errors"
      @close-modal="state.openErrors = null"
    />
    <KkModal :is-visible="!!state.openStores" @change="state.openStores = null">
      <div class="content__header">
        {{ $t('pages.companies.mapionBizSearchGroups.modalTitle') }}
      </div>
      <div class="content__body">
        <div v-for="(s, index) in state.openStores" :key="index" class="item">
          {{ s.name }}
        </div>
      </div>
      <div class="content__footer">
        <div class="c-button">
          <button
            class="c-button__item c-button__item--outline-cancel"
            @click="state.openStores = null"
          >
            {{ $t('labels.close') }}
          </button>
        </div>
      </div>
    </KkModal>
  </div>
</template>

<style lang="scss" scoped>
@import "../../../../../../stylesheets/biz_spa/foundation/_var.scss";
@import "../../../../../../stylesheets/biz_spa/foundation/_mixin.scss";
.templateTable {
  border-spacing: 0 8px !important;
  &__nameHeader {
    width: 25%;
  }
  &__elementHeader {
    width: 30%;
  }
  thead {
    display: none;
  }
  td {
    display: block;
    width: 100%;
    border: 1px solid #E4E7EB;
    font-size: 12px;
  }
  td:not(:first-child) {
    border-top: none;
  }
  td:not(:last-child) {
    border-bottom: none;
  }
  &__tr {
    margin-top: 16px;
  }
  &__tr--closed td {
    background: $color-base-i;
  }
  @include mq() {
    thead {
      display: table-header-group;
    }
    td {
      display: table-cell;
      width: auto;
    }
    &__tr {
      vertical-align: top;
    }
    td:not(:first-child) {
      border-top: 1px solid #E4E7EB;
      border-left: none;
    }
    td:not(:last-child) {
      border-bottom: 1px solid #E4E7EB;
      border-right: none;
    }
  }
  .elementContainer {
    vertical-align: top;
    &__list {
      margin-bottom: 10px;
      padding: 12px;
      border: 1px solid #E4E7EB;
      border-radius: 4px;
    }
  }
  &__name {
    margin-bottom: 15px;
    font-weight: bold;
  }
  &__item {
    margin-bottom: 5px;
  }
}
.addTemplate {
  width: 100%;
  height: 160px;
  margin-top: 32px;
  border-radius: 4px;
  background: $color-base-i;
  color: $color-accent-primary;
  text-align: center;
  svg {
    display: block;
    width: 32px;
    height: 32px;
    margin: 0 auto;
  }
}
.content {
  &__header {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    padding: 20px 32px;
    border-bottom: 1px solid $color-base-c;
    font-size: 16px;
    line-height: 1.5;
  }
  &__body {
    height: 50vh;
    padding: 32px;
    overflow-y: scroll;
    .item {
      margin-bottom: 10px;
    }
  }
  &__footer {
    display: flex;
    justify-content: flex-end;
    padding: 32px;
    border-top: 1px solid $color-base-c;
  }
}
.csvManage {
  margin-top: 15px;
  border: 1px solid #E4E7EB;
  &__header {
    padding: 15px;
    background-color: #F1F1F1;
    font-weight: bold;
  }
  &__contents {
    display: flex;
    .importCsv {
      display: flex;
      width: 50%;
      height: 200px;
      margin: 15px;
      border-radius: 4px;
      background: #F8F9FA;
      color: #E9446A;
      text-align: center;
      &__button {
        margin: auto;
      }
      svg {
        display: block;
        width: 32px;
        height: 32px;
        margin: 0 auto;
      }
    }
  }
}
.downloadContainer {
  margin: auto;
  &__button {
    display: flex;
    margin-bottom: 20px;
    svg {
      display: block;
      width: 20px;
      height: 20px;
      margin-right: 10px;
    }
  }
}
.openModal {
  color: $color-accent-primary;
  text-decoration: underline;
  cursor: pointer;
}
.noTemplates {
  &__button {
    .c-button__item {
      margin: 0;
    }
  }
}
</style>
